// ═══ 일계표 인라인 패널 (말소대행 정산 '장부' 화면 하단) ═══
// 백엔드: GET /settlement-journal/:agencyId/:date[?to=YYYY-MM-DD]  (utils/settlementJournal.loadJournal)
//         → { date, from, to, rows[], totals{}, grandTotal, count }
//   rows[] = { kind:'수출차'|'방문말소'|'방문폐차', refId, date, carNo, party,
//              registrationTax, stampDuty, serviceFee, vatAmount,
//              reissueStampDuty, reissueServiceFee, reissueVat,
//              extraServiceFee, otherFee, otherTaxableFee, subtotal,
//              wonbuFee, reissueFee(=legacy 전용), note }
// PDF: GET /settlement-journal-pdf/:agencyId/:date[?to=] (A4 가로) — openPdf(authFetch blob) 로 연다.
//
// IA (2026-09-02, Jin 3차) — 별도 [일계표] 세그먼트도, 접이식 패널도 아니다.
// **정산 '장부' 화면 전체가 탭**이고 그 중 [일별]/[주별] 탭의 본문이 이 컴포넌트다.
//   탭 바와 탭 상태는 CancelAgencySettlement 가 갖는다 (ledgerTab). 여기는 mode 를 받는 뷰일 뿐.
//   [월별 정산표] 탭에서는 아예 마운트되지 않으므로 그 탭에 있는 동안 요청이 나갈 수 없다.
//
// mode ('day'|'week'):
//   · **조회 월 전체 1회** 조회(1일~말일) 후 클라 그룹핑. 캐시 키 'agencyId|YYYY-MM|스코프'.
//   · 두 탭이 한 인스턴스를 공유하므로 day↔week 왕복에는 요청이 없다.
//     [월별 정산표] 로 나갔다 오면 언마운트돼 다시 묻는다 — 그 탭이 장부를 고치는 자리라 최신화가 맞다.
//   · **집계는 조회 월 안에서만** (Jin 9/2 4차). 데이터가 이미 월 경계인 데다,
//     week 의 월 걸침 주는 기간·라벨·📄 URL 을 전부 조회 월로 클립한다. 안 하면 행에 표시된
//     건수·소계와 그 행의 📄 PDF 내용이 어긋난다 (PDF 는 서버가 기간으로 다시 뽑으므로).
//   · [주별] 한정 '경계 주 전체' 토글 (Jin 9/2 5차, 기본 off·세션 로컬).
//     on 이면 조회 범위를 캘린더 주로 확장(1일이 속한 주의 월요일 ~ 말일이 속한 주의 일요일)해
//     잘린 앞뒤 주를 통째로 본다. 서버는 임의 기간 ?to= 를 이미 받으므로 무변경(PDF 라우트도 동일).
//     클립을 풀었으므로 **합계가 월 장부와 다를 수 있다** — 캡션에 그대로 적는다(조용한 불일치 금지).
//     캐시 키를 분리(…|w)해 토글 왕복 시 각자 캐시. 이웃 달 날짜는 상세에서 흐리게 구분.
//
// 스코프 (2026-09-02, Jin 4차) — 장부에서 고른 거래처를 그대로 따른다.
//   Settlement 가 scopeQs('' | '?companyId=1' | …) 를 넘기고, 여기서 조회 URL 과 캐시 키에 함께 태운다.
//   PDF 도 같은 qs 를 붙인다 — 화면 소계와 인쇄물이 갈리면 안 된다.
//   직원(agency_staff) 가드는 불필요 — 정산 탭 자체가 !isStaff (CancelAgency.js:871).
//
// 컬럼 순서는 서버 PDF(routes/cancelAgency.js COLS)와 동일하게 맞춘다 — 화면과 인쇄물이 어긋나지 않게.
// 구 wonbuFee·reissueFee 는 legacy 행 전용이라 열이 없다 → 둘의 합을 재교부수수료 칸에 괄호로 얹는다.
//
// rangeOf / stepAnchor / rangeLabel / ymdLocal / shiftYmd 는 CancelAgencyDashboard.js 가 import 한다
// (같은 기간 문법의 단일 정의) — 패널에서 ‹› 이동이 사라져도 시그니처·export 는 유지한다.
import React, { useState, useEffect, useRef } from 'react';
import { API, authFetch } from '../utils/api';
import { useIsMobile, hScroll } from '../utils/useMobile';
import MalsoCertViewer from './MalsoCertViewer';
import { JobKindBadge, JOB_KINDS } from './SettlementItemDrawer';
// 미분류 실비 규칙(B-5, 항목 장부와 단일 원천) — 분리 컬럼 도입 전 legacy lump 행의 등록세 칸 괄호 표기.
import { unclassifiedCost } from './CancelAgencySettlement';

var fmt = function (n) { return (parseInt(n, 10) || 0).toLocaleString(); };
// 장부 표기 — 0 은 '-' (정산 장부 표와 동일 문법)
var amt = function (n) { return (parseInt(n, 10) || 0) === 0 ? '-' : fmt(n); };
// 로컬(KST) 기준 YYYY-MM-DD — toISOString 은 UTC 라 오전 9시 전엔 전날로 밀린다 (CancelAgencySettlement ymdLocal 동일 규칙)
export var ymdLocal = function (d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };
// 'YYYY-MM-DD' → 로컬 Date (문자열 그대로 new Date 하면 UTC 해석)
var parseYmd = function (ymd) {
  var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  return m ? new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)) : null;
};
// 'YYYY-MM-DD' ± N일
export var shiftYmd = function (ymd, delta) {
  var d = parseYmd(ymd);
  if (!d) return ymd;
  d.setDate(d.getDate() + delta);
  return ymdLocal(d);
};

// ── 기간 헬퍼 ───────────────────────────────────────────────────────────────
// 대시보드(CancelAgencyDashboard)도 같은 기간 문법을 쓴다 — 헬퍼는 여기서 단일 정의.

// 기준일 → [from, to]. week 는 월~일 (getDay() 0=일 → 월요일까지 되감기).
export var rangeOf = function (mode, anchor) {
  var d = parseYmd(anchor);
  if (!d) return [anchor, anchor];
  if (mode === 'week') {
    var back = (d.getDay() + 6) % 7;      // 월=0 … 일=6
    var mon = new Date(d.getFullYear(), d.getMonth(), d.getDate() - back);
    var sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
    return [ymdLocal(mon), ymdLocal(sun)];
  }
  if (mode === 'month') {
    var first = new Date(d.getFullYear(), d.getMonth(), 1);
    var last = new Date(d.getFullYear(), d.getMonth() + 1, 0);   // 다음달 0일 = 이달 말일
    return [ymdLocal(first), ymdLocal(last)];
  }
  return [anchor, anchor];
};

// ‹ › 이동 — 모드별 하루 / 일주일 / 한 달. 월은 말일 클램프 사고를 피해 1일 기준으로 옮긴다.
export var stepAnchor = function (mode, anchor, delta) {
  if (mode === 'week') return shiftYmd(anchor, 7 * delta);
  if (mode === 'month') {
    var d = parseYmd(anchor);
    if (!d) return anchor;
    return ymdLocal(new Date(d.getFullYear(), d.getMonth() + delta, 1));
  }
  return shiftYmd(anchor, delta);
};

export var rangeLabel = function (mode, from, to) {
  if (mode === 'day') return from;
  if (mode === 'month') {
    var d = parseYmd(from);
    return d ? d.getFullYear() + '년 ' + (d.getMonth() + 1) + '월' : from + ' ~ ' + to;
  }
  return from + ' ~ ' + to;
};

// 'YYYY-MM' → [1일, 말일] / 라벨
export var monthRange = function (month) {
  var m = /^(\d{4})-(\d{2})$/.exec(String(month || ''));
  if (!m) return null;
  var y = parseInt(m[1], 10), mo = parseInt(m[2], 10);
  if (mo < 1 || mo > 12) return null;
  return [ymdLocal(new Date(y, mo - 1, 1)), ymdLocal(new Date(y, mo, 0))];
};
var monthLabel = function (month) {
  var m = /^(\d{4})-(\d{2})$/.exec(String(month || ''));
  return m ? (m[1] + '년 ' + parseInt(m[2], 10) + '월') : String(month || '-');
};

// 금액 열 — 엑셀 장부 열 순서(기타 계열이 재교부 그룹 앞). 서버 PDF COLS 와 동일 순서.
var COLS = [['registrationTax', '등록세'], ['stampDuty', '증지'], ['serviceFee', '수수료'], ['vatAmount', '세액'],
  ['otherFee', '기타'], ['otherTaxableFee', '기타(과세)'], ['extraServiceFee', '부가서비스'],
  ['reissueStampDuty', '재교부증지'], ['reissueServiceFee', '재교부수수료'], ['reissueVat', '재교부세액'],
  ['subtotal', '소계']];
// legacy(원부/구 재교부) 합 — 열이 없어진 값이라 재교부수수료 칸에 괄호로 병기한다.
var legacySum = function (o) { return (parseInt(o.wonbuFee, 10) || 0) + (parseInt(o.reissueFee, 10) || 0); };

var WD = ['일', '월', '화', '수', '목', '금', '토'];

// ── 그룹핑 (순수) ──────────────────────────────────────────────────────────
// 조회 월 rows → 기간 행 [{ key, label, from, to, rows }]. from/to 는 그대로 PDF 기간이 된다.
// gmode='day' 는 날짜별, 'week' 는 월~일. week 은 조회 월 밖으로 나가는 부분을 잘라낸다 —
// 집계를 그 달 안으로 가두라는 계약(Jin 9/2)이자 행 합계 ≡ 그 행 PDF 내용을 지키는 장치.
// ext=true ([주별] '경계 주 전체' 토글) 면 클립하지 않고 캘린더 주(월~일) 그대로 둔다.
export var groupRows = function (gmode, month, rows, ext) {
  var mr = monthRange(month);
  if (!mr) return [];
  var list = rows || [];
  if (!list.length) return [];
  var map = {}, order = [];
  list.forEach(function (r) {
    var d = /^\d{4}-\d{2}-\d{2}$/.test(String(r.date || '')) ? r.date : '';
    var key, from, to, label;
    if (!d) {
      key = ''; from = ''; to = ''; label = '날짜없음';
    } else if (gmode === 'week') {
      var w = rangeOf('week', d);
      from = ext ? w[0] : (w[0] < mr[0] ? mr[0] : w[0]);
      to = ext ? w[1] : (w[1] > mr[1] ? mr[1] : w[1]);
      key = from + '~' + to;
      label = from.slice(5) + ' ~ ' + to.slice(5);
    } else {
      from = d; to = d; key = d;
      label = d.slice(5) + ' (' + WD[parseYmd(d).getDay()] + ')';
    }
    if (!map[key]) { map[key] = { key: key, label: label, from: from, to: to, rows: [] }; order.push(key); }
    map[key].rows.push(r);
  });
  // 날짜 오름차순. 날짜 없는 묶음은 맨 뒤로.
  order.sort(function (a, b) { return a < b ? -1 : (a > b ? 1 : 0); });
  order = order.filter(function (k) { return k !== ''; }).concat(map[''] ? [''] : []);
  return order.map(function (k) { return map[k]; });
};

// '경계 주 전체' 조회 범위 — 1일이 속한 주의 월요일 ~ 말일이 속한 주의 일요일.
export var weekSpanRange = function (month) {
  var mr = monthRange(month);
  if (!mr) return null;
  return [rangeOf('week', mr[0])[0], rangeOf('week', mr[1])[1]];
};

// 기간 소계행 — COLS + legacy 키까지 합산 (합계행의 괄호 병기가 살아 있어야 한다)
var sumRows = function (rows) {
  var t = {};
  (rows || []).forEach(function (r) {
    COLS.forEach(function (c) { t[c[0]] = (t[c[0]] || 0) + (parseInt(r[c[0]], 10) || 0); });
    t.wonbuFee = (t.wonbuFee || 0) + (parseInt(r.wonbuFee, 10) || 0);
    t.reissueFee = (t.reissueFee || 0) + (parseInt(r.reissueFee, 10) || 0);
  });
  return t;
};

var thR = { padding: '8px 6px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', textAlign: 'right' };
var thL = { padding: '8px 6px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', textAlign: 'left' };
var tdR = { padding: '6px', textAlign: 'right' };
var totR = { padding: '8px 6px', textAlign: 'right' };
var legacySt = { color: '#94a3b8', fontSize: 10, marginLeft: 3 };
var pdfBtnSt = { background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', color: '#334155', fontSize: 13, lineHeight: 1, padding: 0, flexShrink: 0 };

function CancelAgencyJournal(props) {
  var agencyId = parseInt(props.agencyId, 10) || 0;
  var showToast = props.showToast || function () {};
  var openPdf = props.openPdf;
  var isMobile = useIsMobile();
  // 동시 작업 점 — 이 패널도 정산 항목(cancel_settlement_items) 목록의 한 형태([일별]/[주별] 탭)라
  // 월별 정산표와 같은 표시를 쓴다. 부모(CancelAgencySettlement.js)의 itemPresenceBadge 를 그대로 받는다.
  // 프레즌스 대상은 소스 A(refId 'si-<id>') 행뿐 — 방문 건('sc-') 은 별도 테이블이라 대상이 아니고,
  // itemId 0 으로 부르면 자리(reserve)만 남아 차량번호 열 정렬이 흔들리지 않는다.
  var presenceBadge = props.presenceBadge || function () { return null; };
  var siIdOf = function (refId) { var m = /^si-(\d+)$/.exec(String(refId || '')); return m ? parseInt(m[1], 10) : 0; };
  var month = String(props.month || '').slice(0, 7);
  var mr = monthRange(month);

  var mode = props.mode === 'week' ? 'week' : 'day';
  // 장부에서 고른 거래처 좌표 — '' | '?accountId=N' | '?companyId=N' | '?clientId=N' (Settlement qsOf)
  var scopeQs = String(props.scopeQs || '');
  var scopeLabel = String(props.scopeLabel || '');
  // 업무 구분 필터 (2026-10-21 Jin) — '' = 전체. 엑셀에서 폐차·이전 시트로 따로 보던 것을 모아 보는 자리.
  // 거래처 스코프와 함께 서버에 ?jobKind= 로 보낸다 (화면 합계와 📄 PDF 가 같은 규칙으로 거른다).
  var [jobKind, setJobKind] = useState('');
  var filterQs = scopeQs + (jobKind ? (scopeQs ? '&' : '?') + 'jobKind=' + encodeURIComponent(jobKind) : '');

  var [cache, setCache] = useState({});     // 'aid|YYYY-MM|scopeQs|ext'
  var [extWeek, setExtWeek] = useState(false); // [주별] 경계 주 전체 (세션 로컬 — 저장 안 함)
  var [expanded, setExpanded] = useState({});
  var [loading, setLoading] = useState(false);
  var [err, setErr] = useState('');
  var cacheRef = useRef({});

  // 경계 주 전체는 [주별] 에서만 의미가 있다 — [일별] 은 언제나 그 달 범위.
  var ext = mode === 'week' && extWeek;
  var span = ext ? weekSpanRange(month) : mr;
  var from = span ? span[0] : '', to = span ? span[1] : '';
  // 스코프·확장이 캐시 키에 들어간다 — 거래처/토글을 바꾸면 미스가 나 자연히 재조회된다.
  var cacheKey = agencyId + '|' + month + '|' + filterQs + '|' + (ext ? 'w' : '');
  // 경로에 이미 ?to= 가 붙으므로 스코프는 & 로 잇는다 (qsOf 는 '?' 로 시작한다)
  var qs = '?to=' + to + (filterQs ? '&' + filterQs.slice(1) : '');

  // 조회 — 같은 (월 × 거래처) 는 캐시 재사용(왕복 시 무요청).
  useEffect(function () {
    if (!agencyId || !from) return undefined;
    if (cacheRef.current[cacheKey]) return undefined;
    var alive = true;
    setLoading(true); setErr('');
    authFetch(API + '/settlement-journal/' + agencyId + '/' + from + qs)
      .then(function (res) { return res.json(); })
      .then(function (d) {
        if (!alive) return;
        // 에러 응답(401/403/400)은 rows 가 없다 — map 크래시 가드
        if (!d || !Array.isArray(d.rows)) { setErr((d && d.error) || '조회 실패'); setLoading(false); return; }
        cacheRef.current[cacheKey] = d;
        setCache(function (c) { var n = Object.assign({}, c); n[cacheKey] = d; return n; });
        setLoading(false);
      })
      .catch(function () { if (!alive) return; setErr('조회 실패'); setLoading(false); });
    return function () { alive = false; };
  }, [agencyId, cacheKey, from, qs]);

  // 탭·월·스코프·확장이 바뀌면 펼침 상태는 의미가 없다 (기간 키가 달라진다)
  useEffect(function () { setExpanded({}); }, [mode, month, filterQs, ext]);

  var data = cache[cacheKey] || null;
  var active = data;                                        // 요약 스트립이 보는 데이터셋
  var groups = groupRows(mode, month, (data && data.rows) || [], ext);

  // 말소증 유무 배지 — 'si-<id>' 행에만. 'sc-'(방문 건) 은 정산 항목이 아니라 대상이 아니다.
  // 조회 월 rows 전체에서 뽑는다(펼침 여부와 무관) — CancelAgencySettlement 장부 표와 같은 배치 조회 관례.
  var [certFlags, setCertFlags] = useState(null);
  var [viewCertItem, setViewCertItem] = useState(null); // {id, carNo}
  var flagIds = ((data && data.rows) || []).map(function (r) { return siIdOf(r.refId); }).filter(function (id) { return id > 0; })
    .filter(function (id, i, arr) { return arr.indexOf(id) === i; }).sort(function (a, b) { return a - b; }).join(',');
  useEffect(function () {
    var ids = flagIds ? flagIds.split(',') : [];
    if (!ids.length) { setCertFlags(null); return undefined; }
    var alive = true;
    var chunks = [];
    for (var i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200));
    Promise.all(chunks.map(function (c) {
      return authFetch(API + '/settlement-items/malso-flags?ids=' + c.join(','))
        .then(function (res) { return res.ok ? res.json() : null; })
        .catch(function () { return null; });
    })).then(function (list) {
      if (!alive) return;
      var merged = {};
      list.forEach(function (d) { if (d) Object.keys(d).forEach(function (k) { merged[k] = d[k] === true; }); });
      setCertFlags(merged);
    });
    return function () { alive = false; };
  }, [flagIds]);
  // si- 행에만 노출. 플래그 true 만 활성 — 행 클릭(하위 상세 펼침)과 안 겹치게 stopPropagation.
  var malsoDocBtn = function (refId, carNo) {
    var sid = siIdOf(refId);
    if (!sid) return null;
    var on = certFlags ? certFlags[sid] === true : false;
    return (<button type="button" className="btn btn-outline btn-sm" disabled={!on}
      title={!certFlags ? '확인 중' : (on ? '말소증 원본 보기' : '말소증 없음')}
      onClick={function (e) { e.stopPropagation(); if (on) setViewCertItem({ id: sid, carNo: carNo }); }}
      style={on ? { fontSize: 11, background: '#ecfdf5', borderColor: '#6ee7b7', color: '#047857', fontWeight: 700 } : { fontSize: 11, color: '#cbd5e1' }}>말소증</button>);
  };

  // 금액 셀 — 등록세 칸은 legacy lump 행(분리 전, baseFee 에 실비가 뭉친 행)의 미분류분을 괄호로 대체 표기,
  // 재교부수수료 칸은 legacy 합을 괄호로 병기 (열이 사라진 구 원부/재교부 값 보존) — 항목 장부와 같은 규칙.
  var moneyCell = function (o, key, zeroDash) {
    if (key === 'registrationTax') {
      var unc = unclassifiedCost(o);
      if (unc > 0) return '(' + fmt(unc) + ')';
    }
    var main = zeroDash ? amt(o[key]) : fmt(o[key]);
    if (key !== 'reissueServiceFee') return main;
    var lg = legacySum(o);
    return (<>{main}{!!lg && <span style={legacySt}>({fmt(lg)})</span>}</>);
  };

  var pdfOf = function (f, t) {
    // 단일일은 기존 경로 그대로(?to 없음) — 서버 하위호환 계약과 동일하게 요청한다.
    // 스코프는 화면과 같은 것을 태운다 (인쇄물이 행 소계와 갈리지 않게).
    var range = (t && t !== f) ? '?to=' + t : '';
    var sq = filterQs ? (range ? '&' + filterQs.slice(1) : filterQs) : '';
    if (openPdf) openPdf('/settlement-journal-pdf/' + agencyId + '/' + f + range + sq);
    else showToast('PDF 열기를 사용할 수 없습니다', 'err');
  };

  // 기간 하위 상세 — 기존 일계표 표 그대로 (구분·차량번호·거래처/의뢰인 + 금액 11열 + 비고 + 말소증)
  var detailTable = function (g) {
    var isRange = g.to !== g.from;
    var colCount = (isRange ? 5 : 4) + COLS.length + 2;
    var tot = sumRows(g.rows);
    return (<div style={isMobile ? hScroll : { overflowX: 'auto' }}>
      <table style={{ width: '100%', minWidth: 1240, borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#1e3a5f', color: '#fff' }}>
          <th style={Object.assign({}, thL, { textAlign: 'center' })}>No</th>
          {!!isRange && <th style={thL}>일자</th>}
          <th style={thL}>구분</th>
          <th style={thL}>차량번호</th>
          <th style={thL}>거래처/의뢰인</th>
          {COLS.map(function (c) { return <th key={c[0]} style={thR}>{c[1]}</th>; })}
          <th style={thL}>비고</th>
          <th style={thL}>말소증</th>
        </tr></thead>
        <tbody>
          {g.rows.map(function (row, i) {
            return (<tr key={(row.kind || '') + '-' + (row.refId || i)} style={{ background: (i + 1) % 2 === 0 ? '#f8fafc' : '#fff', borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '6px', textAlign: 'center' }}>{i + 1}</td>
              {/* 경계 주 전체일 때 이웃 달 날짜는 흐리게 — 이 달 것이 아님을 한눈에 (Jin 9/2 5차) */}
              {!!isRange && <td data-outside={String(row.date || '').slice(0, 7) !== month ? '1' : undefined}
                style={{ padding: '6px', fontSize: 11, whiteSpace: 'nowrap', color: String(row.date || '').slice(0, 7) !== month ? '#cbd5e1' : '#475569' }}>{row.date || '-'}</td>}
              <td style={{ padding: '6px', fontSize: 11, color: '#475569', whiteSpace: 'nowrap' }}>{row.kind || '-'}</td>
              <td style={{ padding: '6px', fontWeight: 700, whiteSpace: 'nowrap' }}>{presenceBadge(siIdOf(row.refId))}{row.carNo || '-'}</td>
              <td style={{ padding: '6px', fontSize: 11, color: '#475569', whiteSpace: 'nowrap' }}>{row.party || '-'}</td>
              {COLS.map(function (c) {
                return <td key={c[0]} style={c[0] === 'subtotal' ? Object.assign({}, tdR, { fontWeight: 700 }) : tdR}>{moneyCell(row, c[0], true)}</td>;
              })}
              <td style={{ padding: '6px', fontSize: 10, color: '#64748b', whiteSpace: isMobile ? 'nowrap' : undefined }}><JobKindBadge kind={row.jobKind} />{row.note || ''}</td>
              <td style={{ padding: '6px', textAlign: 'center' }}>{malsoDocBtn(row.refId, row.carNo)}</td>
            </tr>);
          })}
          <tr style={{ background: '#f0f4f8', fontWeight: 700 }}>
            <td colSpan={isRange ? 5 : 4} style={{ padding: '8px 6px', textAlign: isMobile ? 'left' : 'right' }}>합계 {g.rows.length}건</td>
            {COLS.map(function (c) {
              return <td key={c[0]} style={c[0] === 'subtotal' ? Object.assign({}, totR, { color: '#059669', fontSize: 14 }) : totR}>{moneyCell(tot, c[0], false)}</td>;
            })}
            <td colSpan={2}></td>
          </tr>
          {g.rows.length === 0 && <tr><td colSpan={colCount} style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>내역 없음</td></tr>}
        </tbody>
      </table>
    </div>);
  };

  return (<div>
      {/* 어떤 축을 보고 있는지 정직하게 — 전체인지 고른 거래처 기준인지, 경계 주를 포함했는지. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
          일계표 · {scopeQs ? ('선택 거래처 기준' + (scopeLabel ? ' (' + scopeLabel + ')' : '')) : '대행사 전체 기준'} · {monthLabel(month)}{jobKind ? ' · 업무: ' + jobKind : ''}
          {!!ext && <span style={{ color: '#b45309' }}> · 경계 주 포함 (합계가 월 장부와 다를 수 있음)</span>}
        </span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: '#475569', whiteSpace: 'nowrap' }}>
          업무
          <select aria-label="업무 구분" value={jobKind} onChange={function (e) { setJobKind(e.target.value); }}
            style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#334155' }}>
            <option value="">전체</option>
            {JOB_KINDS.map(function (k) { return <option key={k} value={k}>{k}</option>; })}
          </select>
        </label>
        {/* [주별] 전용 — 월초/월말에 잘린 주를 통째로 본다. 기본 off, 저장하지 않는다. */}
        {mode === 'week' && <label style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={extWeek} onChange={function (e) { setExtWeek(e.target.checked); }} />
          경계 주 전체
        </label>}
      </div>

      {/* 요약 스트립 — 건수 · 합계 금액. [월별] 탭은 그 해 전체, 나머지는 조회 월 전체. */}
      <div style={{ display: 'flex', alignItems: 'stretch', flexWrap: 'wrap', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 120, padding: '9px 12px', borderRight: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>건수</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#334155' }}>{(active && active.count) || 0}건</div>
        </div>
        <div style={{ flex: 1, minWidth: 120, padding: '9px 12px' }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>합계 금액</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1B3A5C' }}>{fmt((active && active.grandTotal) || 0)}원</div>
        </div>
      </div>

      {!!err && <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 12.5, marginBottom: 12 }}>{err}</div>}

      {/* 기간 행 — 클릭하면 그 기간의 하위 상세가 인라인으로 열린다 (여러 행 동시 펼침 허용) */}
      <div style={(loading ? { opacity: 0.55, pointerEvents: 'none' } : undefined)}>
        {groups.map(function (g) {
          var ex = !!expanded[g.key];
          var sub = g.rows.reduce(function (s, r) { return s + (parseInt(r.subtotal, 10) || 0); }, 0);
          return (<div key={g.key} data-testid="journal-period" style={{ border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', marginBottom: 6, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: ex ? '#f0f4f8' : '#fff' }}>
              <button type="button"
                onClick={function () { setExpanded(function (p) { var n = Object.assign({}, p); if (n[g.key]) delete n[g.key]; else n[g.key] = true; return n; }); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', color: '#334155' }}>
                <span style={{ fontSize: 11, color: '#94a3b8', width: 10 }}>{ex ? '▾' : '▸'}</span>
                <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{g.label}</span>
                <span style={{ fontSize: 11.5, color: '#64748b', whiteSpace: 'nowrap' }}>{g.rows.length}건</span>
                <span style={{ marginLeft: 'auto', fontSize: 13.5, fontWeight: 800, color: '#1B3A5C', whiteSpace: 'nowrap' }}>{fmt(sub)}원</span>
              </button>
              <button type="button" title={'기간 PDF ' + g.label} aria-label={'기간 PDF ' + g.label} style={pdfBtnSt}
                onClick={function () { pdfOf(g.from, g.to); }}>📄</button>
            </div>
            {!!ex && <div style={{ borderTop: '1px solid #e5e7eb' }}>{detailTable(g)}</div>}
          </div>);
        })}
        {groups.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          {loading ? '조회 중…' : (jobKind ? '이 달 ' + jobKind + ' 내역 없음' : '이 달 정산 내역 없음')}
        </div>}
      </div>

      {/* 말소증 뷰어 + [업체에 공유] — 상세 표 [말소증] 버튼 (si- 행만) */}
      {!!viewCertItem && <MalsoCertViewer
        title={'말소증' + (viewCertItem.carNo ? ' — ' + viewCertItem.carNo : '')}
        carNo={viewCertItem.carNo}
        fetchDoc={function () { return authFetch(API + '/settlement-items/' + viewCertItem.id + '/malso-doc'); }}
        share={function () { return authFetch(API + '/settlement-items/' + viewCertItem.id + '/malso-share', { method: 'POST' }).then(function (r) { return r.json(); }); }}
        showToast={showToast}
        onClose={function () { setViewCertItem(null); }} />}
  </div>);
}

export default CancelAgencyJournal;
